import {
  DebouncedSearchInput,
  ListHeader,
  ListItem,
  LoadingSpinner as LoadingIndicator,
  Modal,
} from "@msys/ui";
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import {
  BrandAvailabilityScope,
  BrandPreferenceOption,
  filterOptionsByName,
  UseAvailableBrandPreferenceOptions,
} from "../brandPreferenceOptions.js";

interface Props {
  title: string;
  /** Where the offered brands or lines must be available in the catalogue. */
  availableIn: BrandAvailabilityScope;
  useAvailableOptions: UseAvailableBrandPreferenceOptions;
  /** Shown when nothing is available at all, as opposed to nothing matching the search. */
  emptyLabel: string;
  /** How an option is already preferred for the target, or `null`. */
  preferredLabelOf: (id: string) => string | null;
  handleClose: () => void;
  handleComplete: (picked: BrandPreferenceOption[]) => void;
}

/** Picks several brands or brand lines in one session, for the preferences screen. */
export function PickBrandPreferencesModal({
  title,
  availableIn,
  useAvailableOptions,
  emptyLabel,
  preferredLabelOf,
  handleClose,
  handleComplete,
}: Props) {
  const { t } = useTranslate(["Global", "QuoteCreate"]);

  const [searchTerm, setSearchTerm] = React.useState("");
  const [picked, setPicked] = React.useState<BrandPreferenceOption[]>([]);
  const available = useAvailableOptions(availableIn);
  const matching = filterOptionsByName(available.options, searchTerm);

  const pickedIds = new Set(picked.map(option => option.id));
  const toggle = (option: BrandPreferenceOption) =>
    setPicked(current =>
      current.some(candidate => candidate.id === option.id)
        ? current.filter(candidate => candidate.id !== option.id)
        : [...current, option]
    );

  return (
    <Modal
      title={title}
      handleClose={handleClose}
      headerActions={
        picked.length > 0 ? (
          <Typography variant="body2">
            {t("{selectedCount} selected", {
              ns: "Global",
              selectedCount: picked.length,
            })}
          </Typography>
        ) : undefined
      }
      actionButtons={[
        {
          label: t("Cancel", { ns: "Global" }),
          handleClick: handleClose,
          buttonProps: { variant: "text" },
        },
        {
          label: t("Continue", { ns: "Global" }),
          handleClick: () => {
            handleComplete(picked);
            handleClose();
          },
          buttonProps: { disabled: picked.length === 0 },
        },
      ]}
    >
      <Stack direction="column" spacing={1}>
        <ListHeader
          SearchField={
            <DebouncedSearchInput
              placeholder={t("Search", { ns: "Global" })}
              defaultValue={searchTerm}
              onChangeSearchTerm={setSearchTerm}
              autoFocus
              style={{ flex: 1 }}
            />
          }
          FilterChips={
            picked.length > 0 ? (
              <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5 }}>
                {picked.map(option => (
                  <Chip
                    key={option.id}
                    size="small"
                    label={option.name}
                    onDelete={() => toggle(option)}
                  />
                ))}
              </Stack>
            ) : undefined
          }
        />
        <Box sx={{ height: "20em", overflowY: "auto" }}>
          {available.loading ? (
            <LoadingIndicator />
          ) : available.failed ? (
            <EmptyState
              label={t("Something went wrong. Please try again.", {
                ns: "Global",
              })}
            />
          ) : available.options.length === 0 ? (
            <EmptyState label={emptyLabel} />
          ) : matching.length === 0 ? (
            <EmptyState label={t("No results", { ns: "QuoteCreate" })} />
          ) : (
            matching.map(option => {
              const preferredLabel = preferredLabelOf(option.id);
              return (
                <ListItem key={option.id} sx={{ py: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={pickedIds.has(option.id)}
                        onChange={() => toggle(option)}
                        slotProps={{ input: { "aria-label": option.name } }}
                      />
                    }
                    label={
                      <Stack direction="column">
                        <Typography variant="body2">{option.name}</Typography>
                        {preferredLabel !== null ? (
                          <Typography
                            variant="caption"
                            sx={{ color: "grey.600" }}
                          >
                            {preferredLabel}
                          </Typography>
                        ) : null}
                      </Stack>
                    }
                    sx={{ ml: 0, mr: 0, width: "100%" }}
                  />
                </ListItem>
              );
            })
          )}
        </Box>
      </Stack>
    </Modal>
  );
}

function EmptyState({ label }: { label: string }) {
  return (
    <Box sx={{ p: 1, py: 3 }}>
      <Typography align="center" sx={{ color: "grey.500" }} variant="body2">
        {label}
      </Typography>
    </Box>
  );
}
